import { supabase } from '@/integrations/supabase/client';
import { setCurrentSupabaseOrganization } from '@/domains/organizations';
import type {
  Brand,
  BrandMetrics,
  BrandWithMetrics,
  AddBrandData,
} from '../types/brand.types';
import { brandMappers } from '../services/brand.mappers';

export const brandRepository = {
  /**
   * Fetches all brands for an organization or current context.
   */
  async getBrands(orgId?: string): Promise<Brand[]> {
    let query = supabase
      .from('brands')
      .select('id, name, logo_url, industry, website_url, description, organization_id, created_at, updated_at')
      .order('name', { ascending: true });

    if (orgId) {
      query = query.eq('organization_id', orgId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching brands:', error);
      throw error;
    }

    return (data || []).map(brandMappers.mapDbRowToBrand);
  },

  /**
   * Fetches a single brand by ID.
   */
  async getBrandById(id: string, orgId?: string): Promise<Brand | null> {
    let query = supabase
      .from('brands')
      .select('id, name, logo_url, industry, website_url, description, organization_id, created_at, updated_at')
      .eq('id', id);

    if (orgId) {
      query = query.eq('organization_id', orgId);
    }

    const { data, error } = await query.maybeSingle();
    if (error) {
      console.error('Error fetching brand by ID:', error);
      throw error;
    }

    return data ? brandMappers.mapDbRowToBrand(data) : null;
  },

  /**
   * Inserts a new brand record.
   */
  async createBrand(
    brandData: Omit<AddBrandData, 'logo_file' | 'primary_contact_id'> & {
      logo_url: string | null;
      organization_id: string;
    }
  ): Promise<Brand> {
    const { data, error } = await supabase
      .from('brands')
      .insert([brandData])
      .select()
      .single();

    if (error) {
      console.error('Error creating brand in repository:', error);
      throw error;
    }

    return brandMappers.mapDbRowToBrand(data);
  },

  /**
   * Updates an existing brand.
   */
  async updateBrand(id: string, orgId: string, updates: Partial<Brand>): Promise<Brand> {
    const { data, error } = await supabase
      .from('brands')
      .update(updates)
      .eq('id', id)
      .eq('organization_id', orgId)
      .select()
      .single();

    if (error) {
      console.error('Error updating brand in repository:', error);
      throw error;
    }

    return brandMappers.mapDbRowToBrand(data);
  },

  /**
   * Deletes a brand.
   */
  async deleteBrand(id: string, orgId: string): Promise<void> {
    const { error } = await supabase
      .from('brands')
      .delete()
      .eq('id', id)
      .eq('organization_id', orgId);

    if (error) {
      console.error('Error deleting brand:', error);
      throw error;
    }
  },

  /**
   * Fetches metrics for a single brand via RPC.
   */
  async getBrandMetrics(orgId: string, brandId: string): Promise<BrandMetrics> {
    await setCurrentSupabaseOrganization(orgId);

    const { data, error } = await supabase.rpc('get_brand_metrics', {
      p_brand_id: brandId,
    });

    if (error) {
      console.error('Error fetching brand metrics via RPC:', error);
      throw error;
    }

    return brandMappers.mapDbRowToBrandMetrics(data);
  },

  /**
   * Fetches all brand metrics via RPC.
   */
  async getAllBrandMetrics(orgId: string, showOnlyActive: boolean = false): Promise<BrandWithMetrics[]> {
    await setCurrentSupabaseOrganization(orgId);

    const { data, error } = await supabase.rpc('get_all_brand_metrics', {
      show_only_active: showOnlyActive,
    });

    if (error) {
      console.error('Error fetching all brand metrics via RPC:', error);
      throw error;
    }

    return (data || []).map(brandMappers.mapDbRowToBrandWithMetrics);
  },

  /**
   * Fetches brand count for dashboard.
   */
  async getDashboardBrandCount(orgId?: string): Promise<number> {
    let query = supabase
      .from('brands')
      .select('id', { count: 'exact', head: true });

    if (orgId) {
      query = query.eq('organization_id', orgId);
    }

    const { count, error } = await query;
    if (error) {
      console.error('Error fetching dashboard brand count:', error);
      throw error;
    }

    return count ?? 0;
  },
};
